package com.crowpay.views.theming

import com.crowpay.views.components.nav.AppDimensions
import com.lightningkite.kiteui.ViewWrapper
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.views.ViewModifierDsl3
import com.lightningkite.kiteui.views.ViewWriter


data object SectionSemantic : Semantic {
    override val key = "sctn"
    override fun default(theme: Theme): ThemeAndBack {
        val currentIsDark = theme.background.closestColor().toHSP().brightness < 0.95
        return theme.copy(
            id = key,
            background = if (currentIsDark) Color.white else AppColors.grey.light2,
        ).withBack
    }

    object WithoutPadding : ThemeDerivation {
        override fun invoke(theme: Theme): ThemeAndBack = theme[SectionSemantic].theme.withBackNoPadding
    }
}
@ViewModifierDsl3
val ViewWriter.section: ViewWrapper get() = SectionSemantic.onNext

data object LightSection : Semantic {
    override val key: String = "lgtsctn"
    override fun default(theme: Theme): ThemeAndBack {
        val backIsWhite = theme.background.closestColor().perceivedBrightness > 0.99f

        return theme.copy(
            id = key,
            background = if (backIsWhite) AppColors.grey.light3 else Color.white,
        ).withBack
    }
}
@ViewModifierDsl3
val ViewWriter.lightSection: ViewWrapper get() = LightSection.onNext

data object DarkSection : Semantic {
    override val key: String = "drksctn"
    override fun default(theme: Theme): ThemeAndBack {
        val currentIsDark = theme.background.closestColor().toHSP().brightness < 0.95
        return theme.copy(
            id = key,
            background = if (currentIsDark) Color.white else AppColors.grey.light1,
        ).withBack
    }
}

@ViewModifierDsl3
val ViewWriter.darkSection: ViewWrapper get() = DarkSection.onNext

data object DarkBackground : Semantic {
    override val key: String = "drkbck"
    override fun default(theme: Theme): ThemeAndBack {
        return theme.copy(
            id = key,
            background = theme.background.darken(0.3f),
        ).withBackNoPadding
    }
}

data object PurpleSection : Semantic {
    override val key: String = "prpsctn"
    override fun default(theme: Theme): ThemeAndBack = theme.copy(
        id = key,
        foreground = Color.white,
        background = AppColors.primary.main
    ).withBack
}

data object PurpleSelectButtons : Semantic {
    override val key: String = "prpslct"
    override fun default(theme: Theme): ThemeAndBack = theme.copy(
        id = key,
        derivations = mapOf(
            SelectedSemantic to {
                it
                    .copy(
                        id = "psel",
                        background = AppColors.primary.main,
                        foreground = Color.white,
                        outlineWidth = 0.px,
                        outline = Color.transparent
                    )
                    .withBack
            },
            UnselectedSemantic to {
                it
                    .copy(
                        id = "punsel",
                        foreground = AppColors.purple.dark,
                        background = Color.white,
                        outlineWidth = AppDimensions.outlineWidth,
                        outline = AppColors.primary.main
                    ).withBack
            },
        )
    ).withoutBack
}

data class MessageSemantic(val mine: Boolean, val colors: ColorSet): Semantic {
    constructor(mine: Boolean) : this(
        mine,
        AppColors.primary
    )

    override val key: String = buildString {
        append(if (mine) "my" else "other")
        append("msg_")
        append(colors.name)
    }

    override fun default(theme: Theme): ThemeAndBack {
        val newBack = if (mine) colors.main else AppColors.grey.light2
        val newFront = if (mine) Color.white else theme.foreground
        return theme.copy(
            id = key,
            background = newBack,
            cornerRadii = CornerRadii.PerCorner(
                value = 10.dp,
                topLeft = true,
                topRight = true,
                bottomLeft = mine,
                bottomRight = !mine
            ),
            foreground = newFront,
            derivations = mapOf(
                DangerSemantic to {
                    it.copy(
                        id = "dgr",
                        background = Color.white,
                        foreground = Color.black,
                        outline = AppColors.red.main,
                        outlineWidth = 1.px,
                        cornerRadii = CornerRadii.Constant(10.dp),
                        iconOverride = AppColors.red.main
                    ).withBack
                }
            )
        ).withBack
    }
}